/**
 * Frameworks — All compliance frameworks list page
 *
 * Shows every compliance framework (ITAR, CMMC L2, HIPAA, GDPR, SOC 2, ISO 27001,
 * ISO 42001, FINOS AI v2). Each row exposes setup progress, controls health,
 * evidence freshness, risk rating, open findings, and next-assessment date.
 *
 * Architecture follows the Automate/Workflows list (same SortableHeader + grid
 * table pattern) so the whole app feels consistent.
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  ShieldCheck, Plus, Search, FileWarning, Clock, CheckCircle2,
  AlertTriangle, ArrowRight, Lock, Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SortableHeader, useTableSort } from '@/components/ui/data-table';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  FRAMEWORK_STATUS_STYLES,
  RISK_RATING_STYLES,
  FRAMEWORK_CATEGORY_STYLES,
  type Framework,
  type FrameworkId,
  type FrameworkStatus,
  type FrameworkCategory,
} from '@/lib/mock-trust-data';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import {
  FrameworkActivationWizard,
  type ActivationPayload,
} from '@/components/trust/framework-activation-wizard';

export const Route = createFileRoute('/trust/frameworks/')({
  component: FrameworksPage,
});

// ── Helpers ─────────────────────────────────────────────────────────────────

type RiskRating = 'low' | 'medium' | 'high' | 'critical';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diff = d.getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function progressColor(pct: number): string {
  if (pct >= 90) return 'bg-emerald-500';
  if (pct >= 60) return 'bg-amber-500';
  return 'bg-red-500';
}

function progressTextColor(pct: number): string {
  if (pct >= 90) return 'text-emerald-600 dark:text-emerald-400';
  if (pct >= 60) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function passRate(fw: Framework): number {
  const tested = fw.controlsPassing + fw.controlsFailing;
  if (tested === 0) return 0;
  return Math.round((fw.controlsPassing / tested) * 100);
}

function evidenceFreshPct(fw: Framework): number {
  if (fw.evidenceCount === 0) return 0;
  return Math.round((fw.evidenceFresh / fw.evidenceCount) * 100);
}

// ── Page ────────────────────────────────────────────────────────────────────

function FrameworksPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<'all' | FrameworkCategory>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | FrameworkStatus>('all');
  const [riskFilter, setRiskFilter] = useState<'all' | RiskRating>('all');
  const [wizardFramework, setWizardFramework] = useState<Framework | null>(null);

  // Real-data: framework_metadata + framework_tenant_state joined.
  interface ServerFramework {
    id: string; shortName: string; fullName: string; description: string;
    category: string; authority: string; jurisdiction: string;
    controlCount: number; retentionYears: number; updateFrequency: string;
    enforcementDate: string | null; iconColor: string;
    licensed: boolean; enabled: boolean; status: string;
    setupProgress: number; setupCompleted: boolean;
    controlsPassing: number; controlsFailing: number;
    controlsNotApplicable: number; controlsImplemented: number;
    evidenceCount: number; evidenceFresh: number; evidenceStale: number;
    lastAssessmentDate: string | null; nextAssessmentDate: string | null;
    certificationExpiry: string | null; assessor: string | null;
    ownerUserId: string | null; openFindings: number;
    recentChanges: number; riskRating: string; activatedAt: string | null;
  }
  // hyphenated server id → underscored mock FrameworkId enum
  const ID_MAP: Record<string, FrameworkId> = {
    'cmmc-l2': 'cmmc_l2', 'eu-ai-act': 'iso42001',
    'nist-ai-rmf': 'nist_ai_rmf', 'fedramp_moderate': 'fedramp_moderate',
  };
  const { data: realFrameworksResp } = useQuery<{ frameworks: ServerFramework[] }>({
    queryKey: ['frameworks-list'],
    queryFn: () => apiClient.get<{ frameworks: ServerFramework[] }>('/frameworks-list'),
  });
  const FRAMEWORKS: Framework[] = (realFrameworksResp?.frameworks ?? []).map((s): Framework => ({
    id: (ID_MAP[s.id] ?? (s.id as FrameworkId)),
    name: s.shortName,
    shortName: s.shortName,
    fullName: s.fullName,
    description: s.description,
    category: s.category as FrameworkCategory,
    authority: s.authority,
    jurisdiction: s.jurisdiction,
    controlCount: s.controlCount,
    status: s.status as FrameworkStatus,
    licensed: s.licensed,
    enabled: s.enabled,
    activatedAt: s.activatedAt,
    activatedByUserId: null,
    setupCompleted: s.setupCompleted,
    setupAnswers: {},
    setupProgress: s.setupProgress,
    controlsImplemented: s.controlsImplemented,
    controlsPassing: s.controlsPassing,
    controlsFailing: s.controlsFailing,
    controlsNotApplicable: s.controlsNotApplicable,
    evidenceCount: s.evidenceCount,
    evidenceFresh: s.evidenceFresh,
    evidenceStale: s.evidenceStale,
    lastAssessmentDate: s.lastAssessmentDate,
    nextAssessmentDate: s.nextAssessmentDate,
    certificationExpiry: s.certificationExpiry,
    assessor: s.assessor,
    ownerUserId: s.ownerUserId,
    retentionYears: s.retentionYears,
    updateFrequency: s.updateFrequency as Framework['updateFrequency'],
    enforcementDate: s.enforcementDate,
    iconColor: s.iconColor,
    recentChanges: s.recentChanges,
    openFindings: s.openFindings,
    riskRating: s.riskRating as Framework['riskRating'],
  }));

  // Filter
  const filtered = useMemo(() => {
    return FRAMEWORKS.filter(fw => {
      if (!fw.enabled) return false; // hide not-yet-enabled frameworks from the active list
      if (categoryFilter !== 'all' && fw.category !== categoryFilter) return false;
      if (statusFilter !== 'all' && fw.status !== statusFilter) return false;
      if (riskFilter !== 'all' && fw.riskRating !== riskFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        fw.name.toLowerCase().includes(q)
        || fw.fullName.toLowerCase().includes(q)
        || fw.description.toLowerCase().includes(q)
        || fw.authority.toLowerCase().includes(q)
      );
    });
  }, [FRAMEWORKS, search, categoryFilter, statusFilter, riskFilter]);

  const { sorted, sortConfig, handleSort } = useTableSort(filtered, 'name');

  // Activation buckets — for sidebar display
  const activatable = useMemo(() => FRAMEWORKS.filter(fw => fw.licensed && !fw.enabled), [FRAMEWORKS]);
  const locked = useMemo(() => FRAMEWORKS.filter(fw => !fw.licensed), [FRAMEWORKS]);

  // Stats
  const stats = useMemo(() => {
    const enabled = FRAMEWORKS.filter(fw => fw.enabled);
    const total = enabled.length;
    const active = enabled.filter(fw =>
      fw.status === 'in_progress'
      || fw.status === 'setup_in_progress'
      || fw.status === 'audit_ready'
      || fw.status === 'certified',
    ).length;
    const auditReady = enabled.filter(fw =>
      fw.status === 'audit_ready' || fw.status === 'certified',
    ).length;
    const recertDue = enabled.filter(fw => {
      if (fw.status === 'recertification_due') return true;
      const days = daysUntil(fw.certificationExpiry);
      return days !== null && days <= 90;
    }).length;
    return { total, active, auditReady, recertDue };
  }, [FRAMEWORKS]);

  const handleAddFramework = (fw: Framework) => {
    if (!fw.licensed) {
      toast.error(`${fw.shortName} is not included in your tier`, {
        description: 'Contact your account team to upgrade.',
      });
      return;
    }
    setWizardFramework(fw);
  };

  const handleOpenWizard = () => {
    // Default: open wizard for the first activatable framework, else show picker toast
    const firstActivatable = FRAMEWORKS.find(fw => fw.licensed && !fw.enabled);
    if (firstActivatable) {
      setWizardFramework(firstActivatable);
    } else {
      toast.info('No frameworks available to activate', {
        description: 'All licensed frameworks are already enabled.',
      });
    }
  };

  const handleActivate = (payload: ActivationPayload) => {
    // Platform audit 2026-06-12: there is NO POST /frameworks/:id/activate backend
    // yet (governance-frameworks.ts has GET controls + PUT attestation only), so
    // this CANNOT persist enablement. The prior code console.log'd the payload and
    // navigated as if the framework were now active — a fake on a compliance
    // surface. Be honest instead of pretending.
    setWizardFramework(null);
    toast.info('Activation scope captured — enablement is admin-managed', {
      description:
        'Self-serve framework activation is not wired yet; the scope you entered was not persisted. Your platform administrator enables compliance frameworks. Opening the framework for reference.',
    });
    navigate({ to: '/trust/frameworks/$frameworkId', params: { frameworkId: payload.frameworkId as FrameworkId } });
  };

  return (
    <div className="p-6 space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-primary" />
            Frameworks
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every compliance framework you are assessing. Pass rate, evidence freshness, and
            next-assessment dates are tracked continuously.
          </p>
        </div>

        <Button size="sm" className="gap-1.5" onClick={handleOpenWizard} disabled={activatable.length === 0}>
          <Plus className="h-3.5 w-3.5" />
          Activate Framework
        </Button>
      </div>

      {/* ── Stats ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total Frameworks</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {stats.active}
            </p>
            <p className="text-xs text-muted-foreground">Active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {stats.auditReady}
            </p>
            <p className="text-xs text-muted-foreground">Audit Ready</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {stats.recertDue}
            </p>
            <p className="text-xs text-muted-foreground">Recertification Due</p>
          </CardContent>
        </Card>
      </div>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search frameworks, authorities, descriptions..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={categoryFilter} onValueChange={v => setCategoryFilter(v as typeof categoryFilter)}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="security">Security</SelectItem>
            <SelectItem value="privacy">Privacy</SelectItem>
            <SelectItem value="export_control">Export Control</SelectItem>
            <SelectItem value="healthcare">Healthcare</SelectItem>
            <SelectItem value="ai_governance">AI Governance</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => setStatusFilter(v as typeof statusFilter)}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="not_started">Not Started</SelectItem>
            <SelectItem value="setup_in_progress">Setup</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="audit_ready">Audit Ready</SelectItem>
            <SelectItem value="certified">Certified</SelectItem>
            <SelectItem value="recertification_due">Recert Due</SelectItem>
          </SelectContent>
        </Select>
        <Select value={riskFilter} onValueChange={v => setRiskFilter(v as typeof riskFilter)}>
          <SelectTrigger className="w-32 h-9 text-xs"><SelectValue placeholder="Risk" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Risk</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground ml-auto">
          {filtered.length} framework{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Framework Table ────────────────────────────────────────────── */}
      {sorted.length > 0 ? (
        <div className="rounded-lg border overflow-x-auto">
          <div className="min-w-[1380px]">
            {/* Header row */}
            <div className="grid grid-cols-[minmax(320px,2.2fr)_110px_110px_130px_110px_110px_90px_90px_120px_70px] gap-3 px-4 py-3 bg-muted/50 text-xs uppercase tracking-wider">
              <SortableHeader label="Framework" sortKey="name" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Category" sortKey="category" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Status" sortKey="status" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Setup" sortKey="setupProgress" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Controls" sortKey="controlsPassing" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Evidence" sortKey="evidenceFresh" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Risk" sortKey="riskRating" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Findings" sortKey="openFindings" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Next Assessment" sortKey="nextAssessmentDate" currentSort={sortConfig} onSort={handleSort} />
              <span className="text-muted-foreground text-right">Actions</span>
            </div>

            {/* Body rows */}
            <div className="divide-y">
              {sorted.map(fw => {
                const ss = FRAMEWORK_STATUS_STYLES[fw.status];
                const cat = FRAMEWORK_CATEGORY_STYLES[fw.category];
                const risk = RISK_RATING_STYLES[fw.riskRating];
                const evFreshPct = evidenceFreshPct(fw);
                const pass = passRate(fw);
                const daysToAssessment = daysUntil(fw.nextAssessmentDate);
                const assessmentSoon = daysToAssessment !== null && daysToAssessment <= 60;
                const certExpiringSoon = (() => {
                  const d = daysUntil(fw.certificationExpiry);
                  return d !== null && d <= 90;
                })();

                return (
                  <div
                    key={fw.id}
                    role="button"
                    tabIndex={0}
                    className={cn(
                      'grid grid-cols-[minmax(320px,2.2fr)_110px_110px_130px_110px_110px_90px_90px_120px_70px] gap-3 px-4 py-3 items-center hover:bg-muted/30 transition-colors cursor-pointer group focus:outline-none focus:bg-muted/40',
                      fw.status === 'not_started' && 'opacity-75',
                    )}
                    onClick={() => navigate({ to: '/trust/frameworks/$frameworkId', params: { frameworkId: fw.id } } as any)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate({ to: '/trust/frameworks/$frameworkId', params: { frameworkId: fw.id } } as any);
                      }
                    }}
                  >
                    {/* Name + full name + description */}
                    <div className="flex items-start gap-3 min-w-0">
                      <div
                        className={cn(
                          'h-9 w-9 rounded-lg flex items-center justify-center shrink-0 border',
                          fw.iconColor === 'red' && 'bg-red-500/10 border-red-500/20 text-red-600 dark:text-red-400',
                          fw.iconColor === 'blue' && 'bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400',
                          fw.iconColor === 'emerald' && 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400',
                          fw.iconColor === 'violet' && 'bg-violet-500/10 border-violet-500/20 text-violet-600 dark:text-violet-400',
                          fw.iconColor === 'amber' && 'bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400',
                          fw.iconColor === 'pink' && 'bg-pink-500/10 border-pink-500/20 text-pink-600 dark:text-pink-400',
                          fw.iconColor === 'indigo' && 'bg-indigo-500/10 border-indigo-500/20 text-indigo-600 dark:text-indigo-400',
                          fw.iconColor === 'sky' && 'bg-sky-500/10 border-sky-500/20 text-sky-600 dark:text-sky-400',
                          !['red', 'blue', 'emerald', 'violet', 'amber', 'pink', 'indigo', 'sky'].includes(fw.iconColor)
                            && 'bg-primary/10 border-primary/20 text-primary',
                        )}
                      >
                        <ShieldCheck className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-semibold truncate">{fw.name}</p>
                          {fw.recentChanges > 0 && (
                            <Badge
                              variant="outline"
                              className="text-[9px] px-1 py-0 border-blue-500/20 text-blue-600 dark:text-blue-400"
                              title={`${fw.recentChanges} recent changes`}
                            >
                              {fw.recentChanges} new
                            </Badge>
                          )}
                          {certExpiringSoon && (
                            <Badge
                              variant="outline"
                              className="text-[9px] px-1 py-0 border-orange-500/20 text-orange-600 dark:text-orange-400"
                              title={`Certification expires ${formatDate(fw.certificationExpiry)}`}
                            >
                              Recert Soon
                            </Badge>
                          )}
                        </div>
                        <p
                          className="text-xs text-muted-foreground truncate"
                          title={`${fw.fullName} — ${fw.authority} (${fw.jurisdiction})`}
                        >
                          {fw.fullName}
                        </p>
                        <p
                          className="text-[11px] text-muted-foreground/70 truncate mt-0.5"
                          title={fw.description}
                        >
                          {fw.description}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground/70">
                          <span>{fw.controlCount} controls</span>
                          <span>·</span>
                          <span className="truncate">{fw.authority.split(',')[0]}</span>
                        </div>
                      </div>
                    </div>

                    {/* Category */}
                    <Badge variant="outline" className={cn('text-[10px] w-fit', cat.color)}>
                      {cat.label}
                    </Badge>

                    {/* Status */}
                    <Badge variant="outline" className={cn('text-[10px] w-fit', ss.color)}>
                      {ss.label}
                    </Badge>

                    {/* Setup progress with color-coded bar */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className={cn('text-xs font-semibold', progressTextColor(fw.setupProgress))}>
                          {fw.setupProgress}%
                        </span>
                        {fw.setupProgress < 100 && (
                          <span className="text-[10px] text-muted-foreground">in setup</span>
                        )}
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                        <div
                          className={cn('h-full transition-all duration-300', progressColor(fw.setupProgress))}
                          style={{ width: `${fw.setupProgress}%` }}
                        />
                      </div>
                    </div>

                    {/* Controls — passing/total with pass rate */}
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1 text-xs">
                        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                        <span className="font-semibold">{fw.controlsPassing}</span>
                        <span className="text-muted-foreground">/ {fw.controlCount}</span>
                      </div>
                      {fw.controlsFailing > 0 && (
                        <div className="flex items-center gap-1 text-[10px] text-red-600 dark:text-red-400">
                          <AlertTriangle className="h-2.5 w-2.5" />
                          <span>{fw.controlsFailing} failing</span>
                        </div>
                      )}
                      {fw.controlsFailing === 0 && pass > 0 && (
                        <span className={cn('text-[10px]', progressTextColor(pass))}>
                          {pass}% pass
                        </span>
                      )}
                    </div>

                    {/* Evidence — fresh/total */}
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1 text-xs">
                        <span className="font-semibold">{fw.evidenceFresh}</span>
                        <span className="text-muted-foreground">/ {fw.evidenceCount}</span>
                      </div>
                      {fw.evidenceStale > 0 ? (
                        <div className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400">
                          <FileWarning className="h-2.5 w-2.5" />
                          <span>{fw.evidenceStale} stale</span>
                        </div>
                      ) : (
                        <span className={cn('text-[10px]', progressTextColor(evFreshPct))}>
                          {evFreshPct}% fresh
                        </span>
                      )}
                    </div>

                    {/* Risk rating */}
                    <Badge variant="outline" className={cn('text-[10px] w-fit', risk.color)}>
                      {risk.label}
                    </Badge>

                    {/* Open findings */}
                    <div>
                      {fw.openFindings > 0 ? (
                        <div className="flex items-center gap-1">
                          <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />
                          <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                            {fw.openFindings}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          <span className="text-sm font-semibold">0</span>
                        </div>
                      )}
                    </div>

                    {/* Next assessment */}
                    <div className="flex flex-col gap-0.5">
                      <div
                        className={cn(
                          'flex items-center gap-1 text-xs',
                          assessmentSoon && 'text-orange-600 dark:text-orange-400',
                        )}
                      >
                        <Clock className="h-3 w-3" />
                        <span className="truncate">{formatDate(fw.nextAssessmentDate)}</span>
                      </div>
                      {daysToAssessment !== null && (
                        <span className="text-[10px] text-muted-foreground">
                          {daysToAssessment > 0
                            ? `in ${daysToAssessment}d`
                            : daysToAssessment === 0
                              ? 'today'
                              : `${Math.abs(daysToAssessment)}d overdue`}
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div
                      className="flex items-center justify-end"
                      onClick={e => e.stopPropagation()}
                    >
                      <Link
                        {...({
                          to: '/trust/frameworks/$frameworkId',
                          params: { frameworkId: fw.id },
                        } as any)}
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                        title={`Open ${fw.name}`}
                      >
                        Open
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        /* ── Empty state ─────────────────────────────────────────────── */
        <div className="flex flex-col items-center justify-center py-20 text-center border rounded-lg bg-muted/20">
          <ShieldCheck className="h-10 w-10 text-muted-foreground/20 mb-3" />
          <p className="text-sm font-medium">No frameworks match your filters</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-sm">
            {search || categoryFilter !== 'all' || statusFilter !== 'all' || riskFilter !== 'all'
              ? 'Try clearing filters or searching for a framework by name, authority, or description.'
              : 'Enable your first compliance framework to begin tracking controls, evidence, and risk.'}
          </p>
          {(search || categoryFilter !== 'all' || statusFilter !== 'all' || riskFilter !== 'all') ? (
            <Button
              size="sm"
              variant="outline"
              className="mt-4"
              onClick={() => {
                setSearch('');
                setCategoryFilter('all');
                setStatusFilter('all');
                setRiskFilter('all');
              }}
            >
              Clear filters
            </Button>
          ) : (
            activatable.length > 0 && (
              <Button
                size="sm"
                className="mt-4 gap-1.5"
                onClick={handleOpenWizard}
              >
                <Plus className="h-3.5 w-3.5" /> Activate First Framework
              </Button>
            )
          )}
        </div>
      )}

      {/* ── Available to Activate ─────────────────────────────────────────── */}
      {activatable.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-semibold">Available to activate</h2>
            <Badge variant="outline" className="text-[9px]">{activatable.length}</Badge>
            <p className="text-xs text-muted-foreground ml-2">
              Licensed frameworks included in your contract tier, awaiting activation.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {activatable.map(fw => {
              const cs = FRAMEWORK_CATEGORY_STYLES[fw.category];
              return (
                <Card key={fw.id} className="border-dashed hover:border-primary/50 hover:bg-muted/20 transition-colors">
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold">{fw.shortName}</p>
                          <Badge variant="outline" className={cn('text-[9px]', cs.color)}>{cs.label}</Badge>
                        </div>
                        <p className="text-[11px] text-muted-foreground line-clamp-2">{fw.fullName}</p>
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground/70 line-clamp-2 mt-1.5 mb-3">{fw.description}</p>
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-[10px] text-muted-foreground">{fw.controlCount} controls · {fw.authority.split(',')[0]}</span>
                      <Button size="sm" variant="outline" className="text-[10px] h-7 gap-1" onClick={() => handleAddFramework(fw)}>
                        <Sparkles className="h-3 w-3" />
                        Activate
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Locked (requires contract upgrade) ────────────────────────────── */}
      {locked.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-semibold text-muted-foreground">Requires contract upgrade</h2>
            <Badge variant="outline" className="text-[9px]">{locked.length}</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {locked.map(fw => {
              const cs = FRAMEWORK_CATEGORY_STYLES[fw.category];
              return (
                <Card key={fw.id} className="border-dashed opacity-60">
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                          <p className="text-sm font-semibold">{fw.shortName}</p>
                          <Badge variant="outline" className={cn('text-[9px]', cs.color)}>{cs.label}</Badge>
                        </div>
                        <p className="text-[11px] text-muted-foreground line-clamp-2">{fw.fullName}</p>
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground/70 line-clamp-2 mt-1.5 mb-3">{fw.description}</p>
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-[10px] text-muted-foreground">{fw.controlCount} controls</span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-[10px] h-7"
                        onClick={() => toast.info('Contact sales', { description: `Request ${fw.shortName} in your contract tier.` })}
                      >
                        Contact sales
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Activation wizard */}
      <FrameworkActivationWizard
        framework={wizardFramework}
        onClose={() => setWizardFramework(null)}
        onActivate={handleActivate}
      />
    </div>
  );
}

// Export types referenced in this module so other pages can reuse them
export type { FrameworkId };
